import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Building2, MessageSquare, ChevronDown, ChevronRight, User, MapPin } from "lucide-react";
import { ChatRoom } from "@/components/Chat";
import { isOperationalRole, roleLabel } from "@/lib/roles";

/** Mis Servicios del supervisor: objetivos asignados con sus sedes + chat por vigilador. */
export default function SupervisorServicesPanel() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [vigiladores, setVigiladores] = useState([]);
  const [patrolsByClient, setPatrolsByClient] = useState({});
  const [sitesByClient, setSitesByClient] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [chatChannel, setChatChannel] = useState(null);
  const [showAllVigs, setShowAllVigs] = useState({});

  useEffect(() => { (async () => {
    try {
      const [c, p, u] = await Promise.all([
        api.get("/clients"),
        api.get("/patrols"),
        api.get("/users/roster").catch(() => ({ data: [] })),
      ]);
      setClients(c.data);
      setVigiladores((u.data || []).filter((x) => isOperationalRole(x.role) && x.active !== false));

      const map = {};
      (p.data || []).forEach((pa) => {
        if (!map[pa.client_id]) map[pa.client_id] = { general: new Set(), bySite: {} };
        if (pa.site_id) {
          if (!map[pa.client_id].bySite[pa.site_id]) map[pa.client_id].bySite[pa.site_id] = new Set();
          map[pa.client_id].bySite[pa.site_id].add(pa.vigilador_id);
        } else {
          map[pa.client_id].general.add(pa.vigilador_id);
        }
      });
      setPatrolsByClient(map);

      const sitesEntries = await Promise.all(
        (c.data || []).map((cl) => api.get(`/clients/${cl.id}/sites`).then((r) => [cl.id, r.data || []]).catch(() => [cl.id, []]))
      );
      const sitesMap = {}; sitesEntries.forEach(([cid, list]) => { sitesMap[cid] = list; });
      setSitesByClient(sitesMap);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const dmId = (a, b) => "dm:" + [a, b].sort().join(":");
  const resolve = (ids) => (ids || []).map((id) => vigiladores.find((v) => v.id === id)).filter(Boolean);

  return (
    <div style={{ padding: "20px 28px 60px", maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: chatChannel ? "minmax(280px, 380px) 1fr" : "1fr", gap: 20, height: "calc(100vh - 60px)" }}>
      <div style={{ overflowY: "auto" }}>
        <Link to="/supervisor" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }} data-testid="services-panel-title">Mis Servicios</h1>
        <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 14 }}>Objetivos asignados a tu perfil, con sus sedes. Hacé clic para abrir el chat por persona.</div>

        <div data-testid="services-list">
          {clients.length === 0 && <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)" }} data-testid="services-empty">Sin objetivos asignados todavía.</div>}
          {clients.map((c) => {
            const patrolData = patrolsByClient[c.id] || { general: new Set(), bySite: {} };
            const sites = sitesByClient[c.id] || [];
            const generalIds = Array.from(patrolData.general);
            const generalAssigned = resolve(generalIds);
            const shownIds = new Set(generalIds);
            sites.forEach((s) => {
              const union = new Set([...(s.assigned_vigilador_ids || []), ...Array.from(patrolData.bySite[s.id] || [])]);
              union.forEach((id) => shownIds.add(id));
            });
            const others = vigiladores.filter((v) => !shownIds.has(v.id));
            const isOpen = expanded === c.id;
            const showAll = !!showAllVigs[c.id];
            const totalShown = shownIds.size;
            return (
              <div key={c.id} className="card" style={{ marginBottom: 10, padding: 0, overflow: "hidden" }} data-testid={`svc-client-${c.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : c.id)} style={{ width: "100%", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <Building2 size={20} style={{ color: "#BF3437" }}/>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{c.name}{sites.length > 0 && <span style={{ marginLeft: 6, fontWeight: 700, color: "var(--invict-text-muted)", fontSize: 11 }}>· {sites.length} sede(s)</span>}</div>
                    <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{totalShown} persona(s) asignada(s) · {c.address || "Sin dirección"}</div>
                  </div>
                  {isOpen ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
                </button>
                {isOpen && (
                  <div style={{ background: "var(--invict-bone-2)", padding: "8px 14px 14px" }}>
                    <button onClick={() => setChatChannel({ channel_id: `group:${c.id}`, name: c.name + " (Grupal)", kind: "group" })} className="btn-secondary" style={{ width: "100%", padding: "8px", textAlign: "left", display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }} data-testid={`svc-group-${c.id}`}>
                      <MessageSquare size={14} style={{ color: "#BF3437" }}/> Chat grupal del objetivo
                    </button>

                    {sites.map((s) => {
                      const union = Array.from(new Set([...(s.assigned_vigilador_ids || []), ...Array.from(patrolData.bySite[s.id] || [])]));
                      const people = resolve(union);
                      return (
                        <div key={s.id} style={{ marginBottom: 12 }} data-testid={`svc-site-block-${s.id}`}>
                          <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#BF3437", margin: "6px 0 4px", display: "flex", alignItems: "center", gap: 4 }}>
                            <MapPin size={11}/> Sede: {s.name}
                          </div>
                          {people.length === 0 ? (
                            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", padding: 8, textAlign: "center" }} data-testid={`svc-site-empty-${s.id}`}>Sin personal asignado a esta sede.</div>
                          ) : (
                            people.map((v) => (
                              <button key={v.id} onClick={() => setChatChannel({ channel_id: dmId(user.id, v.id), name: v.name, kind: "dm", other_id: v.id, other_role: v.role })} style={{ width: "100%", padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid var(--invict-bone-3)", borderRadius: 3, cursor: "pointer", marginBottom: 5, textAlign: "left" }} data-testid={`svc-dm-site-${s.id}-${v.id}`}>
                                <User size={14} style={{ color: "#0A0A0A" }}/>
                                <div style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{v.name}</div>
                                <span className="badge gray" style={{ fontSize: 9 }}>{roleLabel(v.role).toUpperCase()}</span>
                              </button>
                            ))
                          )}
                        </div>
                      );
                    })}

                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--invict-text-muted)", margin: "6px 0 4px" }}>
                      {sites.length > 0 ? "Sin sede asignada" : "Asignados a este objetivo"}
                    </div>
                    {generalAssigned.length === 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", padding: 8, textAlign: "center" }}>Sin vigiladores asignados. Asigná una ronda primero.</div>}
                    {generalAssigned.map((v) => (
                      <button key={v.id} onClick={() => setChatChannel({ channel_id: dmId(user.id, v.id), name: v.name, kind: "dm", other_id: v.id, other_role: v.role })} style={{ width: "100%", padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid var(--invict-bone-3)", borderRadius: 3, cursor: "pointer", marginBottom: 5, textAlign: "left" }} data-testid={`svc-dm-${v.id}`}>
                        <User size={14} style={{ color: "#0A0A0A" }}/>
                        <div style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{v.name}</div>
                        <span className="badge gray" style={{ fontSize: 9 }}>{roleLabel(v.role).toUpperCase()}</span>
                      </button>
                    ))}

                    {others.length > 0 && (
                      <>
                        <button onClick={() => setShowAllVigs((s) => ({ ...s, [c.id]: !showAll }))}
                          style={{ marginTop: 8, background: "none", border: "1px dashed var(--invict-bone-3)", padding: "6px 10px", fontSize: 11, fontWeight: 700, color: "#0A0A0A", borderRadius: 2, cursor: "pointer", width: "100%", textTransform: "uppercase", letterSpacing: "0.05em" }}
                          data-testid={`svc-toggle-all-${c.id}`}
                        >
                          {showAll ? "Ocultar otro personal" : `Ver todo el personal (${others.length})`}
                        </button>
                        {showAll && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--invict-text-muted)", margin: "0 0 4px" }}>
                              Otro personal del equipo
                            </div>
                            {others.map((v) => (
                              <button key={v.id} onClick={() => setChatChannel({ channel_id: dmId(user.id, v.id), name: v.name, kind: "dm", other_id: v.id, other_role: v.role })}
                                style={{ width: "100%", padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid var(--invict-bone-3)", borderRadius: 3, cursor: "pointer", marginBottom: 5, textAlign: "left", opacity: 0.85 }}
                                data-testid={`svc-other-dm-${v.id}`}
                              >
                                <User size={14} style={{ color: "var(--invict-text-muted)" }}/>
                                <div style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{v.name}</div>
                                <span className="badge gray" style={{ fontSize: 9 }}>{roleLabel(v.role).toUpperCase()}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {chatChannel && (
        <div style={{ background: "#fff", border: "1px solid var(--invict-bone-3)", borderRadius: 4, overflow: "hidden" }}>
          <ChatRoom channel={chatChannel} />
        </div>
      )}
    </div>
  );
}
